import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Grid, Button, TextField, CircularProgress, Box, Paper } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { postCustomer, updateCustomer, fetchCustomer, IPostCustomer, IUpdateCustomer } from 'core/API/customers';
import styles from './CreateCustomer.module.scss';

interface ICustomer {
	fullName: string;
	email: string;
	phone: string;
	companyName: string;
	address: string;
	title: string;
	notes: string;
}

const validationSchema = Yup.object({
	fullName: Yup.string()
		.required('Customer Name is required')
		.min(2, 'Customer Name must be at least 2 characters')
		.max(100, 'Customer Name must not exceed 100 characters'),
	email: Yup.string()
		.required('Email is required')
		.email('Invalid email address')
		.max(100, 'Email must not exceed 100 characters'),
	phone: Yup.string()
		.nullable()
		.max(20, 'Phone number must not exceed 20 characters')
		.test('phone-format', 'Invalid phone number', function(value) {
			if (!value || value.length === 0) return true; // Optional field
			return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(value);
		}),
	companyName: Yup.string()
		.nullable()
		.max(100, 'Company Name must not exceed 100 characters'),
	address: Yup.string()
		.nullable()
		.max(200, 'Address must not exceed 200 characters'),
	title: Yup.string()
		.nullable()
		.max(100, 'Business Title/Position must not exceed 100 characters'),
	notes: Yup.string()
		.nullable()
		.max(1000, 'Notes must not exceed 1000 characters'),
});

const CreateCustomer: React.FC = () => {
	const history = useHistory();
	const location = useLocation<any>();
	const { id } = useParams<{ id?: string }>();
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const isEditMode = !!id;
	
	// Get customer data from location state (if passed from table) or fetch from API
	const customerDataFromState = location.state?.customerData;

	// Fetch customer data if in edit mode and not available in state
	const { data: customerDataFromAPI, isLoading: isLoadingCustomer } = useQuery(
		['customer', id],
		() => fetchCustomer(Number(id!)),
		{
			enabled: isEditMode && !!id && !customerDataFromState,
		}
	);

	// Use state data if available, otherwise use API data
	const customerData = customerDataFromState || customerDataFromAPI;

	const { mutate: createCustomer, isLoading: isCreatingCustomer } = useMutation(postCustomer, {
		onSuccess: () => {
			showToast('success', 'Customer successfully created!');
			queryClient.invalidateQueries(['customers']);
			history.push('/manager/events');
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to create customer');
			setIsSubmitting(false);
		},
	});

	const { mutate: editCustomer, isLoading: isUpdatingCustomer } = useMutation(updateCustomer, {
		onSuccess: () => {
			showToast('success', 'Customer successfully updated!');
			queryClient.invalidateQueries(['customers']);
			history.push('/manager/events');
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to update customer');
			setIsSubmitting(false);
		},
	});

	const formik = useFormik<ICustomer>({
		enableReinitialize: true,
		initialValues: {
			fullName: '',
			email: '',
			phone: '',
			companyName: '',
			address: '',
			title: '',
			notes: '',
		},
		validationSchema: validationSchema,
		onSubmit: (data) => {
			setIsSubmitting(true);
			// Send firstName and lastName as null, use fullName
			const customerPayload: any = {
				firstName: null,
				lastName: null,
				fullName: data.fullName,
				email: data.email,
				phone: data.phone || null,
				companyName: data.companyName || null,
				address: data.address || null,
				title: data.title || null,
				notes: data.notes || null,
			};

			if (isEditMode && id) {
				const updatePayload: any = {
					id: Number(id),
					...customerPayload,
				};
				editCustomer(updatePayload);
			} else {
				createCustomer(customerPayload);
			}
		},
	});

	// Update form values when customerData is loaded
	useEffect(() => {
		if (customerData && isEditMode) {
			const formValues = {
				fullName: customerData.fullName || (customerData.firstName && customerData.lastName ? `${customerData.firstName} ${customerData.lastName}` : '') || '',
				email: customerData.email || '',
				phone: customerData.phone || '',
				companyName: customerData.companyName || '',
				address: customerData.address || '',
				title: customerData.title || '',
				notes: customerData.notes || '',
			};
			formik.setValues(formValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerData, isEditMode]);

	const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

	return (
		<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
			<Box maxWidth="1100px" mx="auto">
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Button
						onClick={() => history.push('/manager/events')}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>

					<Button
						type="submit"
						variant="contained"
						form="createCustomerForm"
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							px: 4,
							textTransform: 'none',
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
						disabled={isSubmitting || isCreatingCustomer || isUpdatingCustomer || (isEditMode && isLoadingCustomer)}
					>
						{isSubmitting || isCreatingCustomer || isUpdatingCustomer ? 'Saving...' : 'Save'}
					</Button>
				</Box>

				{(isLoadingCustomer && isEditMode) ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
						<CircularProgress />
					</Box>
				) : (
				<Paper elevation={1}>
					<Box sx={{ p: 3 }}>
						<h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '24px', color: '#333', marginTop: 0 }}>
							{isEditMode ? 'Edit Customer' : 'Create Customer'}
						</h2>

						<form id="createCustomerForm" onSubmit={handleSubmit} noValidate>
							<Grid container spacing={2}>
								{/* Full Name */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>
										Customer Name <span className={styles.required}>*</span>
									</label>
									<TextField
										name="fullName"
										className={styles.formInput}
										required
										fullWidth
										value={values.fullName}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.fullName && Boolean(errors.fullName)}
										helperText={touched.fullName && errors.fullName}
										placeholder="Enter customer name"
										inputProps={{ maxLength: 100 }}
									/>
								</Grid>

								{/* Email */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>
										Email <span className={styles.required}>*</span>
									</label>
									<TextField
										name="email"
										type="email"
										className={styles.formInput}
										required
										fullWidth
										value={values.email}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Enter email address"
										inputProps={{ maxLength: 100 }}
									/>
								</Grid>

								{/* Phone */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>Phone</label>
									<TextField
										name="phone"
										type="tel"
										className={styles.formInput}
										fullWidth
										value={values.phone}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.phone && Boolean(errors.phone)}
										helperText={touched.phone && errors.phone}
										placeholder="Enter phone number"
										inputProps={{ maxLength: 20 }}
									/>
								</Grid>

								{/* Company Name */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>Company Name</label>
									<TextField
										name="companyName"
										className={styles.formInput}
										fullWidth
										value={values.companyName}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.companyName && Boolean(errors.companyName)}
										helperText={touched.companyName && errors.companyName}
										placeholder="Enter company name"
										inputProps={{ maxLength: 100 }}
									/>
								</Grid>

								{/* Address */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>Address</label>
									<TextField
										name="address"
										className={styles.formInput}
										fullWidth
										value={values.address}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.address && Boolean(errors.address)}
										helperText={touched.address && errors.address}
										placeholder="Enter address"
										inputProps={{ maxLength: 200 }}
									/>
								</Grid>

								{/* Title */}
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>Business Title/Position</label>
									<TextField
										name="title"
										className={styles.formInput}
										fullWidth
										value={values.title}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.title && Boolean(errors.title)}
										helperText={touched.title && errors.title}
										placeholder="Enter business title/position"
										inputProps={{ maxLength: 100 }}
									/>
								</Grid>

								{/* Notes */}
								<Grid item xs={12}>
									<label className={styles.formLabel}>Notes</label>
									<TextField
										name="notes"
										multiline
										rows={4}
										className={styles.formInput}
										fullWidth
										value={values.notes}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.notes && Boolean(errors.notes)}
										helperText={touched.notes && errors.notes}
										placeholder="Enter notes"
										inputProps={{ maxLength: 1000 }}
									/>
								</Grid>
							</Grid>
						</form>
					</Box>
				</Paper>
				)}
			</Box>
		</Box>
	);
};

export default CreateCustomer;

